import { BodyTable, HeaderTable } from "./Tables/Tables";
import { Footer } from "./Components/HTMLFooter";
import {
    IHeaderCol,
    ICoreFrameGridOptions,
    ICoreFrameGrid,
    ISortByCol,
    ICoreFrameGridPagingOptions,
    ICoreFrameGridSummaryRowOptions,
    IRowData,
    IEditingOptions,
    ISortingOptions
} from "./Interfaces";
import { ColumnResizer } from "./ColumnResizer";
import { Constants } from "./Constants";
import { ColumnWidthHelper } from "./Components/ColumnWidthHelper";
import { EventHelper } from "./Components/EventHelper";
import { TotalRowTable } from "./Tables/HTMLTotalRowTable";
import { Utilities } from "./Utilities";

export * from './Interfaces';

export class CoreFrameGrid {
    /**
     * Das Element, in welches sich das Grid rein rendert. Hier können Styles von "außen" existieren.
     */
    private outerWrapper: JQuery<HTMLElement> = undefined;
    /**
     * Ein inneres Element, welches vor allem für den Resizer an der unteren Kante relevant ist. 
     * Ohne den Wrapper könnte der Resizer nicht richtig dargestellt werden.
     */
    private innerWrapper: JQuery<HTMLElement> = undefined;

    private Header: HeaderTable = undefined;
    private Body: BodyTable = undefined;
    private Footer: Footer = undefined;
    private TotalRow: TotalRowTable = undefined;

    private columnWidthsCalculated: boolean = false;

    public dataRows: Array<any> = [];
    private _totalRows: number;
    private _selectedRows: Array<any> = [];
    private headerCols: IHeaderCol[] = undefined;

    private rowData: Set<IRowData>;
    public id: string;
    
    /**
     * Hier werden die sichtbaren Blätter der Spalten gespeichert
     */
    private allVisibleLeafColumns: IHeaderCol[] = undefined;

    private selectedIds:Array<any> = undefined;
    private beforeDataSetSelectedIds: Array<any> = undefined;
    private dataWasFirstTimeExplicitlySet = false;

    private options: ICoreFrameGridOptions;

    private isRendering: boolean;

    private deferredOperations: Array<Function>;

    private resizingElement: JQuery<HTMLElement> = undefined

    private columnResizer: ColumnResizer = undefined;
    private onColumnResizedHandler: (columName: string, newWidth: number) => void;
    private onBeforeRender: () => void;
    private onAfterRender: () => void;
    private onHeightReset: () => void;
    public onBeforeRowRemoved: (row: JQuery<HTMLTableRowElement>) => void;
    private orderBy: ISortByCol[];
    private handler: any;
    private EventHelper: EventHelper = undefined;
    private onUserResize: (newHeight: number) => void;
    private resizeObserver: ResizeObserver;
    public onEditModeChanged: (row: HTMLTableRowElement, entered: boolean) => void;

    public useFillColumn = false;

    private getDefaultOptions(): ICoreFrameGridOptions {
        return {
            headerSortName: 'position',
            rowIdName: 'id',
            height: undefined,
            sortDataName: 'sortstate',
            selectionMode: 'single',
            isEditable: false,
            canAdd: false,
            isEnabled: true,
            doubleClickToEdit: false,
            applyOnLostFocus: false,
            colMinWidth: 50,
            paging: {
                isEnabled: false
            },
            summaryRow: {
                isEnabled: false
            },
            isSortable: false,
            sortingClickStrategy: 'kendo',
            locator: '',
            showHeader: true,
            hasTotalRow: false,
            renderGridAsync: true,
            tooltip: {
                show: true
            },
            isHeightUserAdjustable: false,
            elementTemplates: {},
            defaultHeight: '100%',
            getResource: undefined
        };
    }

    constructor(params: ICoreFrameGrid) {
        this.dataRows = [];
        this.options = this.getDefaultOptions();
        this.onBeforeRender = () => {};
        this.onAfterRender = () => {};
        this.onBeforeRowRemoved = (row) => {}
        this.onUserResize = () => {};
        this.beforeDataSetSelectedIds = [];
        this.deferredOperations = [];

        this.outerWrapper = params.element;
        this.innerWrapper = $('<div>');
        this.innerWrapper.appendTo(this.outerWrapper);
        this.innerWrapper.addClass(Constants.CLASS_PREFIX);
        this.innerWrapper.attr('tabindex', 0);
        this.headerCols = params.columns;
        if(params.data != undefined && Array.isArray(params.data) && params.data.length > 0){
            this.dataRows = params.data;
            this.dataWasFirstTimeExplicitlySet = true;
        }            
        this.options.height = params.height;
        this.id = Math.floor(Math.random() * (100000 - 1) + 1) + 'CFG';
        this.rowData = new Set();

        if(params.colMinWidth !=undefined) 
            this.options.colMinWidth = params.colMinWidth;

        if (params.showHeader != undefined)
            this.options.showHeader = params.showHeader;

        if (params.doubleClickToEdit != undefined)
            this.options.doubleClickToEdit = params.doubleClickToEdit;

        if (params.applyOnLostFocus != undefined)
            this.options.applyOnLostFocus = params.applyOnLostFocus;

        if (params.isEditable != undefined)
            this.options.isEditable = params.isEditable;

        if(params.isSortable)    
            this.options.isSortable = params.isSortable;

        if(params.sortingClickStrategy)
            this.options.sortingClickStrategy= params.sortingClickStrategy;

        if (params.isEnabled != undefined)
            this.options.isEnabled = params.isEnabled;

        if (params.editMode != undefined)
            this.options.editMode = params.editMode;

        if (params.canAdd != undefined)
            this.options.canAdd = params.canAdd;

        if (params.rowIdName != undefined)
            this.options.rowIdName = params.rowIdName;

        if (params.paging != undefined)
            this.options.paging = params.paging;

        if (params.summaryRow != undefined)
            this.options.summaryRow = params.summaryRow;

        if (params.selectionMode != undefined)
            this.options.selectionMode = params.selectionMode;

        if (params.locator != undefined)
            this.options.locator = params.locator;

        if (params.tooltip != undefined) {
            this.options.tooltip = params.tooltip;
        }

        if (params.defaultHeight != undefined)
            this.options.defaultHeight = params.defaultHeight;

        if (params.getResource != undefined)
            this.options.getResource = params.getResource

        this.onColumnResizedHandler = params.onColumnResized;

        if (params.selected != undefined){
            this.selectedIds = params.selected;
        }
        else {
            this.selectedIds = [];
        }

        if(params.onBeforeRender != undefined)
            this.onBeforeRender = params.onBeforeRender;

        if(params.onAfterRender != undefined)
            this.onAfterRender = params.onAfterRender;
        
        if(params.onBeforeRowRemoved != undefined)
            this.onBeforeRowRemoved = params.onBeforeRowRemoved;

        this.handler = {
            onSelectionChangeHandler: params.onSelectionChange,
            onDblClickHandler: params.onDblClick,
            onMouseOverHandler: params.onMouseOver,
            onMouseOutHandler: params.onMouseOut,
            onMouseOverColumn: params.onMouseOverColumn,
            onMouseDownHandler: params.onMouseDown,
            onSortChangeHandler: params.onSortChange,
            onSaveHandler: params.onSave,
            onDeleteHandler: params.onDelete,
            onBeforeEditHandler: params.onBeforeEdit,
            onAddHandler: params.onAdd,
            onKeyDownHandler: params.onKeyDown,
            onFocusLostHandler: params.onFocusLost
        };

        if (params.orderBy)
            this.orderBy = params.orderBy;

        if (params.hasTotalRow)
            this.options.hasTotalRow = params.hasTotalRow;
        
        this.options.renderGridAsync = params.renderGridAsync;

        if (params.isHeightUserAdjustable != undefined)
            this.options.isHeightUserAdjustable = params.isHeightUserAdjustable;

        if (params.onUserResize != undefined)
            this.onUserResize = params.onUserResize;

        if (params.elementTemplates != undefined) {
            this.options.elementTemplates = {
                ...this.options.elementTemplates,
                ...params.elementTemplates
            };
        }

        if (params.onHeightReset != undefined)
            this.onHeightReset = params.onHeightReset;

        this.onEditModeChanged = params.onEditModeChanged || (() => {});

        this.resetWrapperClasses();
    }

    public init() {
        this.EventHelper = new EventHelper(this, this.handler);

        this.Header = new HeaderTable(this.options, this);
        this.Body = new BodyTable(this.options, this);
        this.Footer = new Footer(this.options, this, this.EventHelper);

        // Spaltenköpfe (Header-Tabelle) und Datenzellen (Body-Tabelle) sind getrennte Tabellen.
        // Ohne diese Verknüpfung wäre der Header ein Grid mit Überschriften ohne Datenzellen.
        this.Body.connectHeader(this.Header);

        this.innerWrapper.append(this.Header.getWrapper());
        this.innerWrapper.append(this.Body.getWrapper());

        if(this.options.hasTotalRow){
            this.TotalRow = new TotalRowTable(this.options, this);
            this.innerWrapper.append(this.TotalRow.getWrapper());
        }

        this.innerWrapper.append(this.Footer.getWrapper());

        if (this.options.isHeightUserAdjustable === true) {
            const $resizer = $('<div>');
            $resizer.addClass('bottom-border-resizer');
            $resizer.insertAfter(this.innerWrapper);

            this.resizingElement = $resizer;
        }

        if (this.useLocator())
            this.innerWrapper.attr('data-locator', this.options.locator);

        this.innerWrapper.attr('data-role', 'grid');

        this.setColumns(this.headerCols);
        this.updatePaging(this.options.paging);

        if (this.options.height)
            this.innerWrapper[0].style.height = this.options.height;

        if (this.Header.isHeaderVisible())
            this.innerWrapper.addClass('hasheader');
        else
            this.innerWrapper.addClass('noheader');

        if (this.Footer.isFooterVisible())
            this.innerWrapper.addClass('hasfooter');
        else
            this.innerWrapper.addClass('nofooter');

        if (this.options.isHeightUserAdjustable === true) {
            this.innerWrapper.addClass('vertical-resize');
        }

        this.columnResizer = new ColumnResizer(this);

        // Events
        this.EventHelper.registerAll();

        if (this.options.isHeightUserAdjustable === true) {
            this.EventHelper.registerVerticalGridResizing();
            this.registerUserResizeEvent();
        }

        this.outerWrapper.data('coreframegrid', this);
    }

    private invokeAfterRender(fn: Function): void {
        if (!this.isRendering)
            fn();
        else {
            // wenn eine Funktion benannt ist wird nur der aktuellste Call behalten, um Probleme durch Asynchronität zu vermeiden
            if (fn.name !== '') {
                const index = this.deferredOperations.map(x => x.name).indexOf(fn.name);
                if (index >= 0) {
                    this.deferredOperations[index] = fn;
                    return;
                }
            }
            this.deferredOperations.push(fn);
        }
    }

    private invokeImmediatelyOrDefer(fn: Function): void {
        this.invokeAfterRender(fn);
    }

    private addEditColumn() {
        if (this.options.isEditable === true || (this.options.isEditable !== false && this.options.isEditable.showEditColumn)) {
            let editColumn: IHeaderCol = {
                isEditColumn: true,
                width: '70px',
                hidden: false
            };

            this.headerCols.splice(0, 0, editColumn);
        }
    }
    
    public useLocator() {
        return this.options.locator && this.options.locator != '';
    }

    public getIndexByRowId(id: any): number {
        let indexOfRow = 0;
        this.dataRows.forEach((element, index) => {
            if (element[this.options.rowIdName] == id)
                indexOfRow = index;
        });
        return indexOfRow;
    }

    public getRowIdByIndex(index: number): number {
        let row = this.dataRows[index];
        if (!row)
            return null;

        return row[this.options.rowIdName];
    }

    public getSelectedIds(): Array<string> {
        return this.selectedIds;
    }

    public getResizingElement(): JQuery<HTMLElement> {
        return this.resizingElement;
    }

    public getSelectedData(): Array<any> {
        if (this._selectedRows.length == 0) {
            return [];
        }
        let result = [];
        this._selectedRows.forEach((i) => {
            result.push(this.getRowData(i.dataset[Constants.DATA_ATTRIBUTE_ROW_ID]));
        });

        result = result.filter((r) => {
            return r !== undefined;
        });

        // Sortiert die Daten nach ihrem Index (d.h. ihrer Reihenfolge im DOM)
        if (this.options.rowIdName) {
            result = result.sort((a, b) => {
                let indexA = this.getIndexByRowId(a[this.options.rowIdName]);
                let indexB = this.getIndexByRowId(b[this.options.rowIdName]);

                if (indexA < indexB)
                    return -1;
                else if (indexA > indexB)
                    return 1;
                else
                    return 0;
            });
        }

        return result;
    }

    public getSelectedRows() {
        return this._selectedRows;
    }

    public getRowsInEditMode(): JQuery{
        return $(this.Body.tbody).find('.' + Constants.CLASS_ROW_IN_EDIT);
    }

    public enterEditModeById(rowId: string | number, scrollToELement: boolean = false) {
        if (!this.isRendering)
            this.enterEditModeForRow(rowId, scrollToELement);
        else {
            // darf nur einmal eingefuegt werden (falls zu schnell geklickt wird)
            if (!this.deferredOperations.includes(this.enterEditModeForRow))
                this.deferredOperations.push(() => this.enterEditModeForRow(rowId, scrollToELement))
        }
    }

    private enterEditModeForRow(rowId: string | number, scrollToELement: boolean = false) {
        const $trs = this.Body.getTableRowsByIds([rowId]);
        for (const $tr of $trs) {
            this.Body.enterEditModeFor($tr, scrollToELement).then((row) => {
                this.selectRowElement(row, false, true);
            });
        }
    }

    public selectRowElement(el: HTMLElement, multiSelect: boolean, reset: boolean) {
        const isMultiSelect = multiSelect && this.options.selectionMode == 'multiple';

        if (reset || !isMultiSelect)
            this.deselectAll();

        let id = '';
        el.classList.add(Constants.CLASS_ROW_SELECTED);
        id = el.dataset[Constants.DATA_ATTRIBUTE_ROW_ID];

        if (isMultiSelect) {
            this._selectedRows.push(el);
            this.selectedIds.push(id);
        } else {
            this._selectedRows.push(el);
            this.selectedIds.push(id);
        }
    }

    public selectRowByIds(rowIds: Array<string | number>, multiSelect: boolean = false, reset: boolean = false) {
        let newTrs = this.Body.getTableRowsByIds(rowIds);
        if (reset)
            this.deselectAll();

        newTrs.forEach((el, i) => {
            this.selectRowElement(el, multiSelect, false);
        });
        
        this.optimizeScrollPosition();
    }

    public selectRowByIndex(index: number) {
        let id = this.getRowIdByIndex(index);
        this.selectRowByIds([id], false, true);
    }

    public removeRowById(rowId: string | number) {
        let tr = this.Body.getTableRowsByIds([rowId]);
        if (tr) {
            let arr = this.dataRows;
            this.dataRows = arr.filter((row) => {
                return row[this.options.rowIdName] != rowId;
            });
            if (tr[0].classList.contains(Constants.CLASS_ROW_SELECTED))
                this._selectedRows = [];
            tr[0].remove();
        }
    }

    public setColumns(newColumns: Array<IHeaderCol>) {
        const setColumnData = () => {
            this.onBeforeRender();
            this.headerCols = newColumns;
            // Müssen neu berechnet werden
            this.allVisibleLeafColumns = undefined;
            this.addEditColumn();
            this.Header.fillHeader();
            this.getHeader().setSortExpression(this.orderBy);
            this.beforeDataSetSelectedIds = this.getSelectedIds();
            this.fillBody();
        }

        // wir führen direkt aus oder stellen hier weitere Calls (bspw. durch eine parallele Änderung der Daten hinten an)
        this.invokeImmediatelyOrDefer(() => {
            const selector = `[grid-identifier='${this.Body.getIdentifier()}']`;
            // wenn das Element bereits im DOM steht, müssen wir nicht unnoetig noch auf das Element warten
            if (document.querySelector(selector)) {
                setColumnData();
                return;
            }

            Utilities.waitForDomAppearance(selector).then(() => {
                setColumnData()
            });
        })
    }

    private async fillBody(): Promise<void> {
        this.isRendering = true;
        if (this.options.renderGridAsync)
            await this.refillBodyAsync(); 
        else
            this.refillBodySync();
    }

    private executeDeferredOperations(): void {
        this.deferredOperations.forEach(fn => fn())
        this.deferredOperations = [];
    }

    private refillBodySync() {
        const tableInfo = this.Body.prepareBodySync(this.dataRows);
        this.Body.fillBodySync(tableInfo.rows);
        this.afterFillUpTable(tableInfo.calculatedWidths);
    }

    private async refillBodyAsync() {
        const prepared = this.Body.prepareBodyAsync(this.dataRows);
        prepared.then((tableInfo) => {
            this.Body.fillBodyAsync(tableInfo.rows).then(() => {
                this.afterFillUpTable(tableInfo.calculatedWidths);
            });
        })
    }

    private afterFillUpTable(columnWidths: number[]): void {
        let selectedIds = this.getSelectedIds();
        let beforeDataSetSelectedIds = this.beforeDataSetSelectedIds;
        if (selectedIds.length > 0){
            this.selectRowByIds(selectedIds, true, this.dataWasFirstTimeExplicitlySet);
            const eventHelper = this.getEventHelper();
            if(eventHelper)
                eventHelper.fireSelectionChangedHandler();
        } else if (beforeDataSetSelectedIds.length > 0){
            this.selectRowByIds(beforeDataSetSelectedIds, true, this.dataWasFirstTimeExplicitlySet);
            const eventHelper = this.getEventHelper();
            if(eventHelper)
                eventHelper.fireSelectionChangedHandler();

            beforeDataSetSelectedIds = [];
        }

        // Um den horizontalen Scroller im Grid anzuzeigen, muss bei einem leeren Grid eine leere Zeile hinzugefügt werden
        if (this.dataRows.length == 0) {
            // den Body vom Grid im Vorfeld einmal leeren, sodass durch parallele Calls bereits hinzugefuegte Dummy-Elemente entfernt werden
            this.Body.$tbody.empty();

            let columnLength = this.getAllVisibleLeafColumns().length;
            let dummyRowElement = document.createElement('tr');
            dummyRowElement.classList.add(Constants.CLASS_ROW);
            dummyRowElement.classList.add(Constants.CLASS_DUMMY_ROW);
        
            for (let x = 0; x < columnLength ; x++) {
                let dummyColumn = document.createElement('td');
                dummyColumn.textContent = '&nbsp;';
                dummyRowElement.appendChild(dummyColumn);
            }
        
            if (this.useFillColumn) {
                let fillColumn = document.createElement('td');
                fillColumn.classList.add(Constants.CLASS_CELL_FILLCOLUMN);
                fillColumn.textContent = '&nbsp;';
                dummyRowElement.appendChild(fillColumn);
            }
        
            this.Body.tbody.appendChild(dummyRowElement);
        }
        this.columnResizer?.rearrangeResizer(columnWidths);
    
        this.executeDeferredOperations();
        this.onAfterRender();
        this.isRendering = false;
    }

    public setData(newData: Array<any>) {
        const renderData = () => {
            this.onBeforeRender();
            this.dataRows = newData;
            this.dataWasFirstTimeExplicitlySet = true;
            this.fillBody().then(() => {
                this.Footer.updatePaging(this.options.paging);
                this.Footer.updateSummaryRow();
            });
        }

        // wir führen direkt aus oder behalten hier den aktuellsten weiteren Call (bspw. durch eine parallele Änderung der Daten)
        const setDataInternal = () => {
            const selector = `[grid-identifier='${this.Body.getIdentifier()}']`;
            // wenn das Element bereits im DOM steht, müssen wir nicht unnoetig noch auf das Element warten
            if (document.querySelector(selector)) {
                renderData();
                return;
            }

            Utilities.waitForDomAppearance(selector).then(() => {
                renderData();
            });
        }
        this.invokeImmediatelyOrDefer(setDataInternal);
    }

    public setSelectedIds(selectedIds: Array<number | string>) {
        this.invokeAfterRender(() => this.selectRowByIds(selectedIds, true, true));
    }

    public selectAll(): void {
        this.invokeAfterRender(() => this.selectRowByIds(this.getRowIds(), true, true));
    }

    public deselectRowElement(element: HTMLTableRowElement){
        element.classList.remove(Constants.CLASS_ROW_SELECTED);
        
        this._selectedRows = this._selectedRows.filter(
            (sr: HTMLTableRowElement) => {
                return sr.classList.contains(Constants.CLASS_ROW_SELECTED);
            });

        let rowId = element.dataset[Constants.DATA_ATTRIBUTE_ROW_ID];
        let idIndex = this.selectedIds.indexOf(rowId);
        this.selectedIds.splice(idIndex, 1);        
    }

    public deselectAll(): void {
        this.Body.$tbody.find('.' + Constants.CLASS_ROW).removeClass(Constants.CLASS_ROW_SELECTED);
        this._selectedRows = [];
        this.selectedIds = [];
    }

    public selectPrevious(): { id: any, index: number } {
        if (this.dataRows.length == 0)
            return null;

        let newIndex = 0;
        let indexofFirstSelected = this.getIndexOfFirstSelected();
        if (indexofFirstSelected == 0)
            return { id: this.dataRows[indexofFirstSelected][this.options.rowIdName], index: indexofFirstSelected };
        else if (indexofFirstSelected != null)
            newIndex = indexofFirstSelected - 1;

        this.selectRowByIndex(newIndex);

        return { id: this.dataRows[newIndex][this.options.rowIdName], index: newIndex };
    }

    public selectNext(): { id: any, index: number } {
        if(this.dataRows.length == 0)
            return null;

        let newIndex = 0;
        let indexofFirstSelected = this.getIndexOfFirstSelected();
        if (indexofFirstSelected == this.dataRows.length - 1)
            return { id: this.dataRows[indexofFirstSelected][this.options.rowIdName], index: indexofFirstSelected };
        else if (indexofFirstSelected != null)
            newIndex = indexofFirstSelected + 1;

        this.selectRowByIndex(newIndex);

        return { id: this.dataRows[newIndex][this.options.rowIdName], index: newIndex };
    }

    private getIndexOfFirstSelected(): number {
        if(this._selectedRows.length == 0)
            return null;

        let idOfFirstSelected = this._selectedRows[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID];
        let indexofFirstSelected = this.getIndexByRowId(idOfFirstSelected);

        return indexofFirstSelected;
    }

    public optimizeScrollPosition() {
        if (this._selectedRows.length == 1) {
            let viewTop = this.getBody().getWrapper().scrollTop();
            let viewHeight = this.getBody().getWrapper().height();
            let viewBottom = viewTop + viewHeight;

            let rowTop = this._selectedRows[0].offsetTop;
            let rowHeight = this._selectedRows[0].offsetHeight;
            let rowBottom = rowTop + rowHeight;

            if (rowTop < viewTop)
                this.getBody().getWrapper().scrollTop(rowTop);
            else if (rowBottom > viewBottom)
                this.getBody().getWrapper().scrollTop(rowBottom - viewHeight + this.getBody().getScrollbarThickness());
        }
    }

    public setCanAdd(canAdd: boolean) {
        this.options.canAdd = canAdd;
        const $oldAddButton = this.findAddButton();
        if (canAdd && $oldAddButton.length == 0) {
            const $addButton = this.createAddButton();
            this.innerWrapper.append($addButton)
        }
        else if (!canAdd) {
            $oldAddButton.remove();
        }

        this.resetWrapperClasses();
    }

    private findAddButton(): JQuery {
        return this.innerWrapper.find(`.${Constants.CLASS_ADDBUTTON}`);
    }

    private createAddButton(): JQuery {
        const $addButton = $(this.options.elementTemplates.addButton ?? document.createElement('div'));
        $addButton.addClass(Constants.CLASS_ADDBUTTON);
        return $addButton;
    }

    public setHeight(height: string) {
        this.options.height = height;
        this.innerWrapper[0].style.height = this.options.height;
    }

    public updateHeightBasedOnRows(maxRows: number, additionalHeight: number = 0) {
        this.invokeAfterRender(() => this.setGridHeight(maxRows, additionalHeight));
    }

    private resetGridHeightToDefault() {
        let heightValue = this.options.defaultHeight;
        if (!heightValue) {
            // 100% height as default fallback
            this.setHeight('100%');
            return;
        }

        if (heightValue.includes('%')) {
            this.setHeight(heightValue);
            return;
        }
        
        if (!heightValue.includes('px'))
            heightValue = heightValue + 'px';

        this.setHeight(heightValue);
    }

    private setGridHeight(maxRows: number, additionalHeight: number = 0) {
        const height = this.calculateHeightBasedOnRows(maxRows);
        if (height <= 0)
            return;
    
        this.setHeight(`${height + additionalHeight}px`);
    }

    private calculateHeightBasedOnRows(maxRows: number): number {
        const rowAmount = Math.min(this.getRows().length, maxRows);
        const rowHeight = this.Body.$tbody.children('tr').outerHeight();

        if (rowAmount > 0 && rowHeight <= 0)
            return -1;

        const newBodyHeight = rowHeight * rowAmount;
        const currentBodyHeight = this.Body.getWrapper().outerHeight();
        const otherElementsHeight = this.innerWrapper.outerHeight() - currentBodyHeight;

        return newBodyHeight + otherElementsHeight
    }

    public setIsEnabled(isEnabled: boolean) {
        this.options.isEnabled = isEnabled;

        this.resetWrapperClasses();
    }

    public setSortExpression(orderBy: Array<ISortByCol>)
    {
        this.getHeader().setSortExpression(orderBy);
    }

    public setIsEditable(isEditable: boolean | IEditingOptions) {
        this.options.isEditable = isEditable;

        // Neuzeichnen der Spalten um Editierspalte hinzuzufügen oder zu entfernen
        if (this.headerCols[0] && this.headerCols[0].isEditColumn) {
            this.headerCols.splice(0, 1)
            this.setColumns(this.headerCols);
        }
        else
            this.setColumns(this.headerCols);
    }

    public setIsSortable(isSortable: boolean | ISortingOptions) {
        this.options.isSortable = isSortable;

        // Neuzeichnen des Headers
        this.Header.fillHeader();
    }

    public setSummaryRow(summaryRow: ICoreFrameGridSummaryRowOptions) {
        this.options.summaryRow= summaryRow;
        this.Footer.updateSummaryRow();
    }

    public getAllVisibleRootColumns() {
        return this.headerCols.filter((col) => {
            if (col.hidden === true)
                return false;
            else
                return true;
        });
    }

    public onResizerDblClick() {
        // reset height as default behavior
        this.resetGridHeightToDefault();

        // call hook-method
        if (this.onHeightReset)
            this.onHeightReset();
    }

    public getAllVisibleLeafColumns(): IHeaderCol[] {
        if(this.allVisibleLeafColumns != null){
            return this.allVisibleLeafColumns
        }
        const visibleColumns = this.getAllVisibleRootColumns();
        let allColumnsAreLeafs = false;
        while (!allColumnsAreLeafs && visibleColumns.length > 0) {
            let visibleSubColumns = null;
            for (let x = 0; x < visibleColumns.length; x++) {
                const currentColumn = visibleColumns[x];
                if (!this.columnHasSubColumns(currentColumn)) {
                    if (x == visibleColumns.length - 1)
                        allColumnsAreLeafs = true;
                    continue;
                }

                visibleSubColumns = this.getAllVisibleSubColumnsOfColumn(currentColumn);
                visibleColumns.splice(x, 1, ...visibleSubColumns);
                break;
            }
        }
        this.allVisibleLeafColumns = visibleColumns;
        return this.allVisibleLeafColumns;
    }

    /**
     * Prüft auf sicherem Weg, ob eine Spalte Unterspalten besitzt
     * @param column
     */
    public columnHasSubColumns(column: IHeaderCol){
        return column.subColumns != null && column.subColumns !== undefined && column.subColumns.length > 0;
    }

    /**
     * Gibt alle sichtbaren direkten Unterspalten einer Spalte zurück
     */
    public getAllVisibleSubColumnsOfColumn(column: IHeaderCol): IHeaderCol[]{
        if(!this.columnHasSubColumns(column))
            return [];
        return column.subColumns.filter((col) => col.hidden === false);
    }

    private registerUserResizeEvent(): void {
        const eventHelper = this.getEventHelper();

        // Debouncing damit ein Resizing durch onUserResize nicht erneut zum Auslösen des ResizeObserver-Events führt
        // Siehe https://trackjs.com/javascript-errors/resizeobserver-loop-completed-with-undelivered-notifications/
        const debouncedResizeObserverHandler = Utilities.debounce((entries: ResizeObserverEntry[], observer: ResizeObserver) => {
            if (!entries.length)
                return;

            // Änderungen der Größe, die nicht durch den Benutzer stattfinden, werden ignoriert
            if (!eventHelper.isGridResizeDragActive())
                return;

            const entry = entries[0];

            // Ist das Grid nicht sichtbar, kann es nicht durch den Benutzer geändert worden sein
            if (!$(entry.target).is(':visible'))
                return;

            const height = (entry as any).borderBoxSize[0].blockSize;
            this.onUserResize(height);
        }, 10);
        this.resizeObserver = new ResizeObserver(debouncedResizeObserverHandler);

        this.resizeObserver.observe(this.innerWrapper[0]);
    }

    public resetWrapperClasses() {
        // Grid verfügbar
        if (this.options.isEnabled)
            this.innerWrapper.removeClass(Constants.CLASS_GRID_DISABLED);
        else
            this.innerWrapper.addClass(Constants.CLASS_GRID_DISABLED);

        // EditMode
        if (this.options.editMode == 'single')
            this.innerWrapper.addClass(Constants.CLASS_EDITMODE_SINGLE);
        else
            this.innerWrapper.removeClass(Constants.CLASS_EDITMODE_SINGLE);
        if (this.options.editMode == 'multiple')
            this.innerWrapper.addClass(Constants.CLASS_EDITMODE_MULTIPLE);
        else
            this.innerWrapper.removeClass(Constants.CLASS_EDITMODE_MULTIPLE);

        // Aktuelle Bearbeitung
        if (this.Body) {
            if (this.Body.$tbody.find('tr.' + Constants.CLASS_ROW_IN_EDIT).length > 0)
                this.innerWrapper.addClass(Constants.CLASS_ISEDITING);
            else
                this.innerWrapper.removeClass(Constants.CLASS_ISEDITING);
        }

        // Kann hinzufügen
        if (this.options.canAdd)
            this.innerWrapper.addClass(Constants.CLASS_CANADD);
        else
            this.innerWrapper.removeClass(Constants.CLASS_CANADD);
    }

    public isAddingAllowed(): boolean {
        if (!this.options.isEnabled)
            return false;
        if (this.options.editMode == 'multiple' && this.options.canAdd)
            return true;
        if (this.options.editMode == 'single' && this.options.canAdd && !this.innerWrapper.hasClass(Constants.CLASS_ISEDITING))
            return true;
        else
            return false;
    }

    public isEditingAllowed(): boolean {
        if (!this.options.isEnabled || !this.options.isEditable)
            return false;
        if (this.options.editMode == 'multiple')
            return true;
        if (this.options.editMode == 'single' && !this.innerWrapper.hasClass(Constants.CLASS_ISEDITING))
            return true;
        else
            return false;
    }

    private isDeletingAllowed(): boolean {
        if (!this.options.isEnabled)
            return false;
    }

    public getTotalRows(): number {
        if (this._totalRows)
            return this._totalRows;
        else
            return this.dataRows.length;
    }

    public getRows(): Array<any> {
        return this.dataRows;
    }

    public getRowIds(): Array<any> {
        let ids = [];
        this.getRows().forEach((row) => {
            ids.push(row[this.options.rowIdName]);
        });
        return ids;
    }

    public updatePaging(paging: ICoreFrameGridPagingOptions) {
        this.options.paging = paging;
        this.Footer.updatePaging(paging);
    }

    public getWrapper() {
        return this.innerWrapper;
    }

    public getHeader() {
        return this.Header;
    }

    public getBody() {
        return this.Body;
    }

    public getTotalRow() {
        return this.TotalRow;
    }

    public getOptions() {
        return this.options;
    }

    public getEventHelper() {
        return this.EventHelper;
    }

    public onColumnResized(columnName: string, newWidth: number) {
        let col = this.headerCols.filter((col) => {
            return col.name === columnName}
        );
        if(col.length == 0)
            return;
        
        col[0].widthAutoCalculated = false;
        col[0].width = newWidth;
        ColumnWidthHelper.checkFittedColumn(this, this.getAllVisibleLeafColumns().indexOf(col[0]), col[0])
            
        if (typeof this.onColumnResizedHandler == "function")
            this.onColumnResizedHandler(columnName, newWidth);    
    }

    public setColumnWidth(columnName: string, width: string, withoutEventPropagation: boolean = false): void {
        this.columnResizer.setWidth(columnName, width, withoutEventPropagation);
    }

    public dispose(){
      this.EventHelper.dispose();
      this.resizeObserver?.disconnect();
    }

    public areColumnsFinallyCalculated(): boolean {
        return this.columnWidthsCalculated;
    }

    public checkFittedColumns(): void{
        ColumnWidthHelper.checkFittedColumns(this);
    }

    public refreshTotalRow() {
        this.invokeAfterRender(() => this.TotalRow?.RefreshTotalRow(this.dataRows));
    }

    /**
     * Call this function if the grid header was rendering wrong
     */
    public regenerateColumGroupsIfNeeded(): void {
        const resizer = this.Header.getAllResizer();
        
        if(resizer.first().height() == 0){
            this.columnResizer?.rearrangeResizer([]);
        }
    }

    public storeRowData(rowId: string, data: object): void {
        let rowData: IRowData = {
           rowId: rowId,
           data: data 
        };

        this.rowData.add(rowData);
    }

    public getRowData(rowId: string): object {
        let data: object;
        this.rowData.forEach(entry => {
            if (entry.rowId === rowId) {
                data = entry.data;
                return;
            }
        });

        return data;
    }

    public getRowDataSet(): Set<IRowData> {
        return this.rowData;
    }
}